import BigNumber from "bignumber.js";
import success from "assets/img/success.svg";
import moment from 'moment';
import { useStakingContract, useStakingDispatch } from "context/StakingContract";
import { SET_ADDEES_NOT_STAKED, SET_CONNECTED_ADDRESS, SET_ERD_BALANCE, SET_ERD_BALANCE_BOOL, SET_ETH_BALANCE_BOOL, SET_EXISTING_ALLOWANCE, SET_METAMASK, SET_STAKING_VALUE, SET_TOTAL_STAKE, SET_TOTAL_STAKE_LIMIT, SHOW_STAKE } from "context/StakingContract/actions";
import StakingContract from "context/StakingContract/StakingContract";
import React from "react";

const Success = () => <div className="card">
      <div className="card-body text-center">
          <img
            src={success}
            className="status my-2"
            alt="Success"
          />
          <h3 className="mb-4">Withdrawal successful</h3>
          <p className="mx-5 mb-4">Withdrawal for this address has already been processed</p>
      </div>
    </div>;


const Prerequisites = (props) => {
  const { nextStep } = props;
  const {stakingContract, state} = useStakingContract();
  const dispatch = useStakingDispatch();
  const [stakedAmount, setStakedAmount] = React.useState('0');
  const [stakedDate, setStakedDate] = React.useState('0');
  const [withdrawDate, setInitWithdrawDate] = React.useState('0');


  const thisWorks = () => {
    dispatch({type: SET_METAMASK, payload: true});

    stakingContract.contractTotalStakeLimit()
      .then(val => dispatch({type: SET_TOTAL_STAKE_LIMIT, payload: val}))
      .catch(err => {
        console.warn("error fetching total staking limit", err);
      });
    stakingContract.currentTotalStake()
      .then(val => {
        dispatch({type: SET_TOTAL_STAKE, payload: val});
      })
      .catch(err => {
        console.warn("error fetching total stake", err);
      });
    stakingContract.getStakeDeposit()
      .then(res => {
        setStakedAmount(res[0].toString());
        setStakedDate(res[1].toString());
        setInitWithdrawDate(res[2].toString());
    }).catch(err => {
      console.warn("error get stake deposit", err);
    });

    // 4. Check if connected account has ERD and ETH balances
    stakingContract.provider.getSigner().getAddress().then(addr => {
      dispatch({type: SET_CONNECTED_ADDRESS, payload: addr});

      stakingContract.ethBalance().then(ethBalance => {
        if ( ethBalance > new BigNumber(0) ) {
          dispatch({type: SET_ETH_BALANCE_BOOL, payload: true});
        } else {
          console.log("smaller ETH balance");
        }
      }).catch(e => console.error(e));

      stakingContract.erdBalance().then(erdBalance => {
        if ( erdBalance > new BigNumber(0) ) {
          dispatch({type: SET_ERD_BALANCE_BOOL, payload: true});
          dispatch({type: SET_ERD_BALANCE, payload: erdBalance});
          dispatch({type: SET_STAKING_VALUE, payload: erdBalance});
        } else {
          console.log("smaller ERD balance");
        }
      }).catch(e => console.error(e));

      // Check if address is already staked:
      stakingContract.alreadyStaked().then(staked => {
        dispatch({type: SET_ADDEES_NOT_STAKED, payload: !staked});
        if ( staked ) {
          return;
        }

        stakingContract.getAllowance().then(allowance => {
          if ( allowance > 0 ) {
            dispatch({type: SET_EXISTING_ALLOWANCE, payload: allowance});
          }
        }).catch(e => console.error(e));
      }).catch(e => console.error(e));
    });
  };

  const checkPrerequisites = () => {
    dispatch({type: SHOW_STAKE, payload: false});
    // 1. Check metamask installed!
    if ( !StakingContract.hasMetamask() ) {
      console.log("user does not have metamask installed");
      return;
    }
    console.log("user has metamask installed");

    // 2. Check if metamask is unlocked
    stakingContract.metamaskEnabled().then(isEnabled => {
      // 3. Enable metamask
      if ( !isEnabled ) {
        // This will show metamask popup
        StakingContract.enableMetamask()
          .then(_ => {
            thisWorks();
          })
          .catch(e => {
            console.warn("user rejected metamask");
          });
          return;
      }
      thisWorks();
    });
  };

  React.useEffect(checkPrerequisites, []);

  const {prerequisites} = state;
  const allowInitiate = stakedAmount !== '0' && stakedDate !== '0';
  const differenceInSeconds = (date) => moment().utc().diff(moment.unix(date), 'seconds') > 60 * 60 * 24 * 7;
  const sevenDaysPassed = withdrawDate !== '0' && differenceInSeconds(withdrawDate);

  const canGoToTheNextStep = (allowInitiate || sevenDaysPassed) && prerequisites.metamask;

  const nextStepValidation = canGoToTheNextStep ? nextStep : () => false;

  const continueButtonProps = {
    onClick: nextStepValidation,
    ...((withdrawDate !== '0' && !sevenDaysPassed) || !canGoToTheNextStep ? {disabled: true} : {})
  };

  const Component = () => <div className="card">
    <div className="card-body">
      <h3>Prerequisites</h3>
      <p className="lead">Before proceeding with the withdrawal we want to confirm that the following conditions are met:</p>

      <ul className="prerequisites mb-4">
        <li>
          <span className={prerequisites.metamask ? '' : 'd-none'}>
            <em className="text-success">✓</em>
            MetaMask installed
          </span>
          <span className={prerequisites.metamask ? 'd-none' : ''}>
            <em className="text-danger">✗</em>
            MetaMask not installed
          </span>
        </li>
        {
          stakedAmount === '0' && stakedDate.toString() ?
            <li>
              <em className='text-success'>✓</em>
              Staked {parseInt(stakingContract.fromWei(stakedAmount, 'ether')).toLocaleString('en')} ERD on
              &nbsp;{moment.unix(stakedDate).format("Do of MMMM")} UTC
            </li>
            :
            <li>
              <em className='text-danger'>✗</em>
              No stake found for this address
            </li>
        }
        <li>
          <span className={prerequisites.ethBalance ? '' : 'd-none'}>
            <em className="text-success">✓</em>
            ETH balance greater than 0 <small>(only required for gas fees)</small>
          </span>
          <span className={prerequisites.ethBalance ? 'd-none' : ''}>
            <em className="text-danger">✗</em>
            ETH balance is 0 <small>(only required for gas fees)</small>
          </span>
        </li>
      </ul>
      <button className="btn btn-primary mb-1" {...continueButtonProps}>
        Continue
      </button>
      <br/>
      {
        withdrawDate !== '0' && !sevenDaysPassed &&
        <small>
          You will be able to proceed with the final withdrawal step on the <strong>{moment.unix(withdrawDate).add(7, 'days').format("Do of MMMM HH:mm UTC")}</strong>, 
          7 days after the withdrawal initialization was made.
        </small>
      }
    </div>
  </div>;

  return withdrawDate !== '0' && stakedAmount === '0' ? <Success/> : <Component />;
};

export default Prerequisites;
